/**
 * Claim-level reliability tagging (RV pack, RV5).
 *
 * Surfaces per-sentence reliability for narrative claims using an
 * inline token format that mirrors the existing `[Ref N]` discipline:
 *
 *   [verified]                  — provider data / uploaded doc / named source
 *   [verified Ref 3]            — verified AND tied to a specific reference
 *   [inferred regional baseline]— LLM-derived from related data / industry default
 *   [inferred]                  — short-hand; no per-claim source string
 *   [unverified]                — no underlying data; LLM-from-training-corpus
 *
 * The renderer (walkClaimText) parses both `[Ref N]` and these
 * reliability tokens, emitting <RefLink> + <ReliabilityBadge> JSX. The
 * LLM is instructed (reportPrompts.ts) to emit a reliability tag on
 * every load-bearing factual claim — so a reviewer reading a sentence
 * with a `inferred` glyph immediately knows it's weighted as inference
 * rather than measurement.
 *
 * Why a parse-pass rather than a structured response field: the
 * existing prompt + schema architecture is sentence-level free text
 * with `[Ref N]` tokens woven in. Re-using that pattern means zero
 * schema churn and the same render-time parsing logic that already
 * exists for citations.
 */

/** Three-tier per-claim reliability surfaced in the rendered narrative. */
export type ClaimReliability = 'verified' | 'inferred' | 'unverified';

/** Parsed reliability token from a narrative string. */
export interface ReliabilityToken {
  reliability: ClaimReliability;
  /** Free-text qualifier the LLM emitted after the tier
   *  (e.g. "regional baseline", "industry benchmark"). May be empty. */
  qualifier: string;
  /** When the token also carried `Ref N`, the 1-based reference index. */
  refN?: number;
}

/** Regex matching a reliability token — see file docstring for syntax. */
export const RELIABILITY_TOKEN_RE =
  /\[(verified|inferred|unverified)(?:\s+(?:Ref\s+(\d+)|([^\]]+)))?\]/gi;

/** Inclusive split regex for `walkClaimText` — captures both reliability
 *  tokens and `[Ref N]` tokens so the renderer keeps the original prose
 *  intact between matches. */
export const CLAIM_SPLIT_RE =
  /(\[(?:verified|inferred|unverified)(?:\s+(?:Ref\s+\d+|[^\]]+))?\]|\[Ref\s+\d+\])/gi;

/** Parse a single match from `CLAIM_SPLIT_RE`. Returns null if the
 *  match isn't a reliability token (e.g. `[Ref 3]`). */
export function parseReliabilityToken(raw: string): ReliabilityToken | null {
  const single = new RegExp(RELIABILITY_TOKEN_RE.source, 'i');
  const m = raw.match(single);
  if (!m) return null;
  const tier = m[1]?.toLowerCase() as ClaimReliability;
  const refStr = m[2];
  const qualifier = (m[3] ?? '').trim();
  return {
    reliability: tier,
    qualifier,
    ...(refStr ? { refN: Number(refStr) } : {}),
  };
}

/** True when the raw match is a `[Ref N]` token rather than a reliability tag. */
export function isPlainRefToken(raw: string): boolean {
  return /^\[Ref\s+\d+\]$/i.test(raw);
}

/** Extract just the N from a plain `[Ref N]` token. Returns NaN on miss. */
export function extractRefN(raw: string): number {
  const m = raw.match(/^\[Ref\s+(\d+)\]$/i);
  return m ? Number(m[1]) : NaN;
}

/** Strip every reliability tag from a string, leaving plain prose
 *  (and any [Ref N] tokens) untouched. Used by exporters / search
 *  indexers that want clean prose. */
export function stripReliabilityTokens(text: string): string {
  return text.replace(RELIABILITY_TOKEN_RE, '').replace(/\s{2,}/g, ' ').trim();
}

/**
 * PG-9b (Golden-D/E) — STANDALONE reliability tokens only.
 *
 * Matches a reliability tier marker that carries NO `Ref N` payload —
 * i.e. the SSM body-guard's `[unverified inferred-from-context]` chip
 * (ssmBodyGuard.ts), `[verified]`, `[inferred regional baseline]`, etc.
 * The negative lookahead `(?!Ref\s+\d+\s*\])` deliberately EXCLUDES the
 * citation-bearing forms (`[verified Ref 3]`, `[inferred Ref 2]`, …) so
 * the office exporters can drop the bracket-noise chip without also
 * eating a numbered citation that still has to lift to a footnote /
 * resolve to a reference. The VIEW renders the standalone token as a
 * small reliability badge; the DOCX/PPTX serializers have no badge
 * primitive in body prose, so they strip the literal bracket instead of
 * leaking it as raw text.
 */
export const STANDALONE_RELIABILITY_TOKEN_RE =
  /\[(?:verified|inferred|unverified)(?:\s+(?!Ref\s+\d+\s*\])[^\]]+)?\]/gi;

/**
 * PG-9b — strip the standalone reliability chips from prose bound for
 * DOCX/PPTX body text, leaving every `[Ref N]` (and `[<tier> Ref N]`)
 * citation marker intact for the downstream footnote / reference
 * lifting. Collapses the doubled whitespace the removed chip leaves
 * behind (it is emitted as `chip + ' ' + [Ref N]`) so the surrounding
 * prose reads cleanly. Idempotent + safe on clean text.
 */
export function stripStandaloneReliabilityTokens(text: string): string {
  if (!text || text.indexOf('[') === -1) return text;
  return text
    .replace(STANDALONE_RELIABILITY_TOKEN_RE, '')
    // Tidy the whitespace the removed chip leaves: a leading space before
    // a now-orphaned `[Ref N]`, and any doubled spaces mid-sentence.
    .replace(/\s+([.,;:])/g, '$1')
    .replace(/[ \t]{2,}/g, ' ');
}

/**
 * Section-level Watch / Risk banner copy generator. When a section is
 * gated by a significant or critical gap, prepend a banner to the
 * rendered body so the reader sees the constraint up front rather
 * than discovering it via badge-only signals on individual sentences.
 *
 * Returns null when no banner is warranted.
 */
export interface SectionWatchSignal {
  tier: 'watch' | 'risk';
  /** 1-sentence why — e.g. "WRI Aqueduct unavailable for this site". */
  reason: string;
  /** Optional remediation copy — e.g. "Upload water assessment →". */
  remediationCopy?: string;
}

export function buildWatchBannerText(signal: SectionWatchSignal): string {
  const lead =
    signal.tier === 'risk'
      ? 'This section is unreliable for siting decisions —'
      : 'This section runs on regional baseline —';
  return `${lead} ${signal.reason}. Specific claims below should be independently verified.`;
}
